import PropTypes from 'prop-types'
import { answerPropType } from '@aytm/app/stats/page/question-views/components/lib/prop-types'
import { useMemo } from 'react'

import { isNoneChartMode } from '@aytm/app/stats/page/question-views/components/lib/chart-modes'
import { isComponentOn } from '@aytm/app/stats/page/question-views/components/lib/utils'

import { calcChartProps } from '@aytm/app/stats/page/question-views/oex/lib/props/chart'
import {
  buildAnswerVariantProps,
  calcAnswerVariantsProps
} from '@aytm/app/stats/page/question-views/oex/lib/props/variants'
import { calcTableProps } from '@aytm/app/stats/page/question-views/oex/lib/table-utils'
import { buildCommentsClick } from '@aytm/app/stats/page/question-views/answer-variants/lib/table-utils'
import { calcSortOrder } from '@aytm/app/stats/page/question-views/oe/lib/table-utils'
import useOexState from '@aytm/app/stats/page/question-views/oex/states/useOexState'
import { calcChartPlaceholderHeight } from '@aytm/app/stats/page/question-views/answer-variants/components/chart/lib/placeholder'

import Items from '@aytm/app/stats/page/question-views/oex/components/Items'
import Chart from '@aytm/app/stats/page/question-views/answer-variants/components/chart/Chart'
import ChartPlaceholder from '@aytm/app/stats/page/question-views/components/charts/placeholders/Chart'
import Table from '@aytm/app/stats/components/data-views/table/components/Table'
import BlockWrapper from '@aytm/app/stats/page/question-views/components/BlockWrapper'

// The extended open-ended block: a chart over the answer variants, the list of those variants as a
// table, and the expandable list where each open variant draws its own open-ended block.
//
// block.coffee owns the mount, the report-param write and the payload, and the per-variant blocks
// that own each variant's params. Everything the researcher sees and moves is here, and the block
// answers for its whole subtree through the api it hands up.
const Block = props => {
  const {
    id,
    questionKey,
    type,
    format,
    components,
    answer,
    answerVariants,
    answerType,
    clickable,
    questionChartMode,
    defaultSortOrder,
    reportPercentDecimals,
    labelTarget,
    handlers,
    onReady,
    onStateChange
  } = props

  const {
    chartMode,
    zoomLevel,
    sortOrder,
    uncollapsed,
    onChartReady,
    onChartModeChange,
    onZoomChange,
    onSortChange,
    onAnswerVariantReady,
    onAnswerVariantStateChange,
    onAnswerVariantWcParams,
    loadingRows,
    onToggle,
    onToggleAll
  } = useOexState({
    params: props.params,
    paramsRevision: props.paramsRevision,
    questionKey,
    type,
    format,
    answer,
    answerVariants,
    questionChartMode,
    defaultSortOrder,
    reportPercentDecimals,
    labelTarget,
    onReady,
    onStateChange,
    onAnswerVariantParamsChange: props.onAnswerVariantParamsChange,
    onAnswerVariantWcParamsChange: props.onAnswerVariantWcParamsChange,
    onUpdateData: props.onUpdateData
  })

  // The props an open variant's block draws from. It is a question of its own as far as the report
  // params go, so it announces its own changes and this block forwards them to the shell.
  const oePropsOf = useMemo(
    () =>
      buildAnswerVariantProps({
        questionId: id,
        questionKey,
        type,
        format,
        components,
        answer,
        modelDefaults: props.modelDefaults,
        clickable,
        questionChartMode,
        defaultSortOrder,
        reportPercentDecimals,
        batch: labelTarget?.batch,
        link: props.link,
        isReportXpert: props.isReportXpert,
        answerVariantParams: props.answerVariantParams,
        loadingRows,
        handlers: {
          ...handlers,
          onAnswerVariantReady,
          onAnswerVariantStateChange,
          onAnswerVariantWcParamsChange: onAnswerVariantWcParams
        }
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      id,
      questionKey,
      type,
      format,
      components,
      answer,
      clickable,
      questionChartMode,
      defaultSortOrder,
      reportPercentDecimals,
      labelTarget,
      handlers,
      onAnswerVariantReady,
      onAnswerVariantStateChange,
      onAnswerVariantWcParams,
      props.modelDefaults,
      props.link,
      props.isReportXpert,
      props.answerVariantParams,
      loadingRows
    ]
  )

  // the chart hands its api to both sides: the shell serializes the svg, the state reads the axis
  const chartHandlers = useMemo(
    () => ({
      ...handlers,
      onChartModeChange,
      onZoomChange,
      onChartReady: api => {
        onChartReady(api)
        handlers?.onChartReady?.(api)
      }
    }),
    [handlers, onChartModeChange, onZoomChange, onChartReady]
  )

  // a clicked column names the order it wants; the state is what settles on one
  const tableHandlers = useMemo(
    () => ({
      ...handlers,
      onSort: (colIndex, dir, cols) => {
        const order = calcSortOrder(cols, colIndex, dir)
        if (order) onSortChange(order)
      },
      onCommentsClick: buildCommentsClick({
        id,
        questionId: props.questionId || id,
        link: props.link,
        answerVariants,
        isReportXpert: props.isReportXpert
      })
    }),
    [handlers, id, props.questionId, props.link, answerVariants, props.isReportXpert, onSortChange]
  )

  const question = { ...props, chartMode, zoomLevel, sortOrder, uncollapsed }

  const chartProps = calcChartProps({ ...question, handlers: chartHandlers })

  // how tall the frame is held open until the chart is scrolled to; a mode that draws no chart at
  // all -- a map, a word cloud -- has no frame to hold open and answers null
  const chartHeight = calcChartPlaceholderHeight({
    ...chartProps,
    itemCount: (chartProps.batches || chartProps.answerVariants)?.length || 0
  })

  // the variants list closes the block, so the response count sits under it rather than under the
  // chart; a block drawing no list carries the count itself
  const chartOn = isComponentOn(components, 'chart') && !isNoneChartMode(questionChartMode, chartProps.globalChartMode)
  const tableOn = isComponentOn(components, 'table')
  const summaryOn = chartOn || tableOn
  const itemsOn = isComponentOn(components, 'fields')

  // The reference link and the line closing the block -- the response count, or the footer standing
  // in for it -- belong to whichever half draws last. A block whose summary is off hands them to the
  // list rather than opening an empty section above it just to hold them.
  const closingProps = summaryOn ? { answer, footer: props.footer } : props

  return (
    <>
      {summaryOn ? (
        <BlockWrapper {...props}>
          {chartOn && (
            <ChartPlaceholder height={chartHeight} isLoading={'loading' === answer}>
              <Chart {...chartProps} />
            </ChartPlaceholder>
          )}
          {tableOn && (
            <Table
              {...calcTableProps({
                id,
                questionId: id,
                type,
                format,
                components,
                answer,
                answerVariants,
                answerType,
                sortOrder,
                clickable,
                handlers: tableHandlers
              })}
            />
          )}
        </BlockWrapper>
      ) : null}

      {itemsOn || !summaryOn ? (
        <BlockWrapper {...closingProps}>
          {itemsOn ? (
            <Items
              {...calcAnswerVariantsProps({ ...question, oePropsOf })}
              onToggle={onToggle}
              onToggleAll={onToggleAll}
            />
          ) : null}
        </BlockWrapper>
      ) : null}
    </>
  )
}

Block.displayName = 'OexBlock'

// What the shell hands in: the question, the sections of the report params the block owns, the
// props each open variant's own block resolved, and the handlers the surfaces report back through.
export const BLOCK_PROP_TYPES = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  questionKey: PropTypes.string,
  type: PropTypes.string,
  format: PropTypes.object,
  components: PropTypes.object,
  answer: answerPropType,
  answerVariants: PropTypes.array,
  answerType: PropTypes.string,
  answerLabels: PropTypes.array,
  unitLabel: PropTypes.string,
  batches: PropTypes.array,
  aType: PropTypes.string,
  plotLines: PropTypes.array,
  degree: PropTypes.number,
  scaleTmpl: PropTypes.string,
  scalePow: PropTypes.number,
  scaleSteps: PropTypes.number,
  clickable: PropTypes.bool,
  questionChartMode: PropTypes.string,
  defaultSortOrder: PropTypes.string,
  params: PropTypes.shape({
    cm: PropTypes.string,
    z: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
    s: PropTypes.string,
    cb: PropTypes.string
  }),
  // bumped by the shell every time it re-reads the params off the model
  paramsRevision: PropTypes.number,
  labelTarget: PropTypes.object,
  link: PropTypes.string,
  isReportXpert: PropTypes.bool,
  modelDefaults: PropTypes.object,
  // the report params every variant owns, keyed by position
  answerVariantParams: PropTypes.object,
  handlers: PropTypes.object,
  onReady: PropTypes.func,
  onStateChange: PropTypes.func,
  onAnswerVariantParamsChange: PropTypes.func,
  onAnswerVariantWcParamsChange: PropTypes.func,
  onUpdateData: PropTypes.func
}

Block.propTypes = BLOCK_PROP_TYPES

export default Block
